import gql from 'graphql-tag';
import graphqlClient from '@/helpers/graphqlClient';
import { applicationLoanTypes } from '@/helpers/constants';
import { isObject, getObjectFromArray } from '@/helpers/utils';

export default new (class {
	async getCategoryData(expenseCategoryID, userData, includePasscodes) {
		const { studentId, studentLifeStageId, studentChildrenCount } = userData;
		const { data } = await graphqlClient.query({
			query: gql`
      {
        cmsExpenseCategories(where: "expenseCategory_ID=\\"${expenseCategoryID}\\" && isActive=true") {
          shoppingRule
          expenseCategory_ID
          researchHeader
          researchDidYouKnowText
          researchDidYouKnowSource
          isCompletedForCurrentStudent
					lowestExpenseSubCategoryCost
					highestExpenseSubCategoryCost
          cmsExpenseCategoryResearches {
            title
            description
            imageBase64
          }
          cmsExpenseCategoryLogos {
            expenseCategoryLogo_ID
            imageBase64
          }
          studentExpenseCategoryBudgets(
            where: "StudentId=${studentId}"
          ) {
            budget
          }
          cmsExpenseCategoryTabs {
            name
          }
          cmsExpenseSubCategories(where:"CmsExpenseSubCategoryLifeStages.Any(it.LifeStage_ID==\\"${studentLifeStageId}\\") && isActive=true") {
            monthlyLoanAmount
						monthlyLoanDealershipAmount
            expenseSubCategory_ID
            cost
            costPerChild
            description
            imageBase64
            name
            isMandatory
            additionalCost
            showMontlyCost
            canChooseQuantity
            cmsExpenseCategoryTab {
              name
            }
            subcategories: studentExpenseSubCategoryExpenditures(where: "lifeStage_ID = \\"${studentLifeStageId}\\" && StudentId=${studentId}") {
              expenseSubCategory_Id
              lifeStage_ID
              amountSpent
            }
          }
          cmsUpperBudgetGuidelines(where: "CmsChildStatus.NumberOfChildren == ${studentChildrenCount}") {
            minimum
            maximum
          }
        }
        categoryPasscode: studentExpenseCategories(where: "studentId = ${studentId} && isUnlockedWithPasscode == true") {
          id
        }
      },
      `,
			fetchPolicy: 'no-cache'
		});

		if (data && data.cmsExpenseCategories.length) {
			let result = data.cmsExpenseCategories[0];
			// If there is something we have to use it.
			if (data.categoryPasscode.length && includePasscodes) {
				result.categoryPasscodeId = data.categoryPasscode[0].id;
			}

			return result;
		}

		return null;
	}

	async getLoanApplicationCategoryData(expenseCategoryID) {
		const { data } = await graphqlClient.query({
			query: gql`
      {
        cmsExpenseCategories(where: "expenseCategory_ID=\\"${expenseCategoryID}\\" && isActive=true") {
          researchHeader
          researchDidYouKnowText
          researchDidYouKnowSource
          cmsExpenseCategoryResearches {
            title
            description
            imageBase64
          }
          cmsExpenseCategoryLogos {
            expenseCategoryLogo_ID
            imageBase64
          }
				},
      }`,
			fetchPolicy: 'no-cache'
		});

		if (data && data.cmsExpenseCategories.length) {
			let result = data.cmsExpenseCategories[0];

			return result;
		}

		return null;
	}

	async addOrUpdateStudentCategory(list, skippedPurchaseId, deletionIds) {
		return await graphqlClient.mutate({
			mutation: gql`
				mutation(
					$add: StudentExpenseCategoryAddInputObjectGraphType!
					$subCategoriesIds: [Int]!
					$skippedId: Int!
				) {
					deleteSubcategories: deleteManyStudentExpenseSubCategoryExpenditure(
						ids: $subCategoriesIds
					)
					deleteSkippedCategory: deleteStudentExpenseCategory(id: $skippedId)
					addStudentExpenseCategory(add: $add) {
						id
					}
				}
			`,
			variables: {
				add: list,
				skippedId: skippedPurchaseId,
				subCategoriesIds: deletionIds
			}
		});
	}

	async updateCategoryBudget(data) {
		let list = [];

		list.push(data);

		if (Array.isArray(data)) {
			list = data;
		}

		return await graphqlClient.mutate({
			mutation: gql`
				mutation(
					$expenseCategoryBudgets: [StudentExpenseCategoryBudgetInputObjectGraphType]!
				) {
					addOrUpdateIgnoreNullValuesManyStudentExpenseCategoryBudget(
						list: $expenseCategoryBudgets
					) {
						id
					}
				}
			`,
			variables: {
				expenseCategoryBudgets: list
			}
		});
	}

	async getCategoryGroups(userData, billboards) {
		// TODO: refactor this to use proper styles, not template objects
		return await graphqlClient.query({
			query: gql`{
        cmsExpenseCategoryGroups {
          name
          cmsExpenseCategories (where: "IsActive == true && CmsExpenseCategoryGroup.IsActive == true && CmsUpperSimulationTemplateExpenseCategories.Any(it.UpperSimulationTemplate_ID==\\"${
						userData.upperSimulationTemplate_ID
					}\\") ") {
          name
		  defaultName
          assetName
          expenseCategory_ID
          transitionHeading
          transitionSupportText
          isActive
          isMandatoryForCurrentStudent
          ${billboards ? '' : 'billboardLogoBase64'}
          isCompletedForCurrentStudent
          isPasscodeLockedForCurrentStudent
          isLockedForCurrentStudent
          amountSpentForCurrentStudent
          passcodeForCurrentArea
          studentExpenseCategoryBudgets(where: "StudentId=${
						userData.studentId
					} && lifeStage_Id=\\"${userData.studentLifeStageId}\\"") {
            id
            budget
            expenses
            expenseCategory_Id
            isAutomaticallyCreated
          }
          cmsUpperBudgetGuidelines(where: "CmsChildStatus.NumberOfChildren == ${
						userData.studentChildrenCount
					}") {
            minimum
            maximum
          }
          }
        }
        }
      `,
			fetchPolicy: 'no-cache'
		});
	}

	async getPurchasesForCategory(options) {
		const { studentId, lifeStageId, subCategoriesIds, categoryId } = options;
		const filters = [];
		const subcategories = [];
		const result = {
			skippedPurchase: {},
			subcategories: []
		};

		let categoryFilters = [];

		filters.push(`studentId = ${studentId}`);
		filters.push(`lifeStage_ID = "${lifeStageId}"`);

		categoryFilters = [...filters];

		// combine all ids into one query
		subCategoriesIds.forEach(item => {
			subcategories.push(`expenseSubCategory_Id="${item}"`);
		});

		filters.push(`(${subcategories.join('||')})`);

		// set Expense categoryId for skip category check
		categoryFilters.push(`expenseCategory_ID="${categoryId}"`);

		const { data } = await graphqlClient.query({
			query: gql`
				query CategoryPurchases($where: String!, $categoryWhere: String!) {
					subcategories: studentExpenseSubCategoryExpenditures(where: $where) {
						id
						expenseSubCategory_Id
						lifeStage_ID
						amountSpent
						adultCount
						childCount
						cmsPaymentMethod {
							upperPaymentMethod_ID
							name
							defaultName
						}
					}
					skippedPurchase: studentExpenseCategories(where: $categoryWhere) {
						noPurchaseIsMade
						id
					}
				}
			`,
			variables: {
				where: filters.join('&&'),
				categoryWhere: categoryFilters.join('&&')
			},
			fetchPolicy: 'no-cache'
		});

		if (data) {
			result.skippedPurchase = data.skippedPurchase[0] || {};
			result.subcategories = data.subcategories.map(i => {
				return {
					id: i.id,
					expenseSubCategoryId: i.expenseSubCategory_Id,
					adultQty: i.adultCount,
					childQty: i.childCount,
					paymentMethod:
						i.cmsPaymentMethod != null
							? i.cmsPaymentMethod.upperPaymentMethod_ID
							: null
				};
			});
		}

		return result;
	}
	async makePurchase(subCategories, deletionIds, skippedPurchaseId) {
		const { data } = await graphqlClient.mutate({
			mutation: gql`
				mutation(
					$list: [StudentExpenseSubCategoryExpenditureAddInputObjectGraphType]!
					$ids: [Int]!
					$skippedId: Int!
				) {
					deleteSkippedCategory: deleteStudentExpenseCategory(id: $skippedId)
					delete: deleteManyStudentExpenseSubCategoryExpenditure(ids: $ids)
					add: addManyStudentExpenseSubCategoryExpenditure(list: $list) {
						id
						expenseSubCategory_Id
						studentId
					}
				}
			`,
			variables: {
				skippedId: skippedPurchaseId,
				ids: deletionIds,
				list: subCategories
			}
		});

		if (!data.add) {
			return false;
		}

		return true; // All subCategories were processed successfully
	}
	/**
	 * Update a category as unlocked when passcode is required
	 *
	 * @param  {string} categoryId
	 * @param  {number} studentId
	 *
	 * @returns {promise}
	 */
	async updatePasscodeRequirement(categoryId, studentId) {
		console.log('updatePasscodeRequirement called');
		const categoryData = {
			expenseCategory_ID: categoryId,
			studentId: studentId,
			isUnlockedWithPasscode: true
		};

		return await graphqlClient.mutate({
			mutation: gql`
				mutation($add: StudentExpenseCategoryAddInputObjectGraphType!) {
					updatePasscode: addStudentExpenseCategory(add: $add) {
						id
					}
				}
			`,
			variables: {
				add: categoryData
			}
		});
	}
	/**
	 * Get Loan categoryId based on their assetName.
	 * It uses a constant for mapping the types
	 *
	 * @param  {string} facilityId
	 * @param  {applicationLoanTypes|String} loanType
	 *
	 * @returns {object|null}
	 */
	async getLoanCategories(facilityId) {
		const mapAssetName = {
			[applicationLoanTypes.AUTO_LOAN]: 'auto-loan-transportation',
			[applicationLoanTypes.AUTO_LOAN_BANKING_AND_FINANCE]:
				'auto-loan-banking-and-finance',
			[applicationLoanTypes.MORTGAGE_LOAN]: 'mortgage-loan'
		};

		const filters = [];

		filters.push(
			`(assetName="auto-loan-transportation" || assetName="mortgage-loan" || assetName="auto-loan-banking-and-finance")`
		);
		filters.push(`cmsExpenseCategoryGroup.facility_ID=${facilityId}`);

		const { data } = await graphqlClient.query({
			query: gql`
				query loanApplication($where: String!) {
					loanCategory: cmsExpenseCategories(where: $where) {
						expenseCategory_ID
						assetName
						transitionHeading
						transitionSupportText
						cmsExpenseCategoryGroup {
							facility_ID
						}
					}
				}
			`,
			variables: {
				where: filters.join('&&')
			},
			fetchPolicy: 'no-cache'
		});

		const result = {
			autoLoan: {
				expenseCategory_ID: ''
			},
			autoLoanBankingAndFinance: {
				expenseCategory_ID: ''
			},
			mortgageLoan: {
				expenseCategory_ID: ''
			}
		};

		if (data.loanCategory.length) {
			result.autoLoan = data.loanCategory.find(
				i => i.assetName === mapAssetName[applicationLoanTypes.AUTO_LOAN]
			);
			result.autoLoanBankingAndFinance = data.loanCategory.find(
				i =>
					i.assetName ===
					mapAssetName[applicationLoanTypes.AUTO_LOAN_BANKING_AND_FINANCE]
			);
			result.mortgageLoan = data.loanCategory.find(
				i => i.assetName === mapAssetName[applicationLoanTypes.MORTGAGE_LOAN]
			);
		}
		return result;
	}

	/**
	 * Verify if a user has applied for a loan
	 *
	 * @param  {Integer} studentId
	 * @param  {Object} loanCategoryIds
	 * @param  {String} lifeStageId
	 * @param {Boolean} shouldReturn
	 */
	async hasAppliedForLoan(studentId, categoriesIds, lifeStageId, shouldReturn) {
		const filters = [];

		filters.push(`studentId=${studentId}`);

		if (isObject(categoriesIds)) {
			filters.push(
				`(expenseCategory_ID="${categoriesIds.autoLoan}" || expenseCategory_ID="${categoriesIds.autoLoanBankingAndFinance}" ||expenseCategory_ID="${categoriesIds.mortgageLoan}")`
			);
		} else {
			filters.push(`expenseCategory_ID="${categoriesIds}"`);
		}

		filters.push(`lifeStage_ID="${lifeStageId}"`);

		const { data } = await graphqlClient.query({
			query: gql`
				query loanApplication($where: String!) {
					hasApplication: studentExpenseCategories(where: $where) {
						id
						hasApplied: appliedForLoan
						expenseCategory_ID
						loanDisplayedInShoppingPhase
					}
				}
			`,
			variables: {
				where: filters.join('&&')
			},
			fetchPolicy: 'no-cache'
		});

		const defaultValues = {
			autoLoan: null,
			mortgageLoan: null,
			autoLoanBankingAndFinance: null
		};

		let autoLoanApplication = {
			id: 0,
			hasApplied: false,
			loanDisplayedInShoppingPhase: false
		};
		let mortgageLoanApplication = {
			id: 0,
			hasApplied: false,
			loanDisplayedInShoppingPhase: false
		};
		let autoLoanBankingApplication = {
			id: 0,
			hasApplied: false,
			loanDisplayedInShoppingPhase: false
		};

		if (data.hasApplication.length) {
			if (isObject(categoriesIds)) {
				const autoLoan = getObjectFromArray(
					data.hasApplication,
					'expenseCategory_ID',
					categoriesIds.autoLoan
				);

				if (autoLoan) {
					autoLoanApplication = {
						...autoLoanApplication,
						...autoLoan,
						loanDisplayedInShoppingPhase:
							!autoLoan.loanDisplayedInShoppingPhase && autoLoan.hasApplied
					};
				}

				const autoLoanBankingAndFinance = getObjectFromArray(
					data.hasApplication,
					'expenseCategory_ID',
					categoriesIds.autoLoanBankingAndFinance
				);

				if (autoLoanBankingAndFinance) {
					autoLoanBankingApplication = {
						...autoLoanBankingApplication,
						...autoLoanBankingAndFinance,
						loanDisplayedInShoppingPhase:
							!autoLoanBankingAndFinance.loanDisplayedInShoppingPhase &&
							autoLoanBankingAndFinance.hasApplied
					};
				}

				const mortgageLoan = getObjectFromArray(
					data.hasApplication,
					'expenseCategory_ID',
					categoriesIds.mortgageLoan
				);

				if (mortgageLoan) {
					mortgageLoanApplication = {
						...mortgageLoanApplication,
						...mortgageLoan,
						loanDisplayedInShoppingPhase:
							!mortgageLoan.loanDisplayedInShoppingPhase &&
							mortgageLoan.hasApplied
					};
				}
			} else {
				// TODO: check this as might not be needed
				const result = data.hasApplication[0];
				// used afterwards because if it is null or false we should trigger the modal
				defaultValues.loanDisplayedInShoppingPhase = !result.loanDisplayedInShoppingPhase;
				defaultValues.hasAppliedForAutoLoan = result.appliedForLoan;
				defaultValues.id = result.id;
			}
		}
		defaultValues.autoLoanBankingAndFinance = autoLoanBankingApplication;
		defaultValues.autoLoan = autoLoanApplication;
		defaultValues.mortgageLoan = mortgageLoanApplication;
		return shouldReturn ? defaultValues : !!data.hasApplication.length;
	}

	/**
	 * Add new studentExpenseCategory with a appliedForLoan property
	 * To mark a Loan Category as applied
	 *
	 * @param  {Number} studentId
	 * @param  {String} loanCategoryId
	 * @param  {String} lifeStageId
	 *
	 * @returns Boolean
	 */
	async applyForLoan(studentId, loanCategoryId, lifeStageId) {
		const loanCategoryObject = {
			expenseCategory_ID: loanCategoryId,
			studentId: studentId,
			lifeStage_ID: lifeStageId,
			appliedForLoan: true
		};

		const { data } = await graphqlClient.mutate({
			mutation: gql`
				mutation($add: StudentExpenseCategoryAddInputObjectGraphType!) {
					applyForLoan: addStudentExpenseCategory(add: $add) {
						id
					}
				}
			`,
			variables: {
				add: loanCategoryObject
			}
		});

		if (data.applyForLoan) {
			return true;
		}

		return false;
	}

	async updateLoansModalVisibility({ id: resourceId }) {
		const loanCategory = {
			id: resourceId,
			loanDisplayedInShoppingPhase: true
		};

		const { data } = await graphqlClient.mutate({
			mutation: gql`
				mutation($update: StudentExpenseCategoryInputObjectGraphType!) {
					updateLoansModalVisibility: updateStudentExpenseCategory(
						update: $update
					) {
						id
					}
				}
			`,
			variables: {
				update: loanCategory
			}
		});

		return !!data.updateLoansModalVisibility.length;
	}
})();
